import React, { useState, useEffect, useLayoutEffect, useRef } from "react";
import { useSelector } from "react-redux";
import get from "lodash/get";
import { object, number, string } from "prop-types";
import { StoryElement, WithLazy } from "@quintype/components";

import Tags from "../story-elements/tags";
import MoreNews from "../story-elements/more-news";
import AlsoRead from "../story-elements/also-read";
import Slideshow from "../story-elements/slide-show";
import Attachment from "../story-elements/attachment";
import FactCheckView from "../story-elements/fact-check";
import PodcastBody from "../story-elements/podcast-body";
import ImageGallery from "../story-elements/image-gallery";
import SponsoredTag from "../story-elements/sponsored-tag";
import MovieReviewView from "../story-elements/movie-review";
import GadgetReviewView from "../story-elements/gadget-review";
import ReferenceElement from "../story-elements/reference-element";

import ImagewithCaptionWithoutShare from "../../molecules/image-without-share";

import { AdContainer } from "../../ads";
import TaboolaAd from "../../ads/taboola-ad";
import NewBodyWidget from "../../atoms/new-body-banner";

import BlockQuoteIcon from "../../atoms/icons/blockquote-icon";
import PromotionalMessage from "./promotional-message";
import ImagewithCaption from "../../molecules/Image-caption";

import LatestNews from "./latest-news";
import useDevice from "../../helper/custom-hook/useDevice";
import useAdType from "../../helper/custom-hook/useAdType";

import "./story-elements.m.css";
import QandA from "./q-and-a";
import PaywallCard from "../../molecules/paywall";
import { SpinnerLoader } from "../../common/loader";

let countPara = 0;
let hasRenderedMidAd1 = false;
let hasRenderedMidAd2 = false;
const getParagraphsArr = (html) => {
  const parser = new DOMParser();
  const doc = parser.parseFromString(html, "text/html");
  const elements = [...doc.body.children];
  return elements.map((el) => el.outerHTML);
};
let syndicatedPrintString = "ಮೂಲತಃ ಪ್ರಜಾವಾಣಿ ಮುದ್ರಣ ಆವೃತ್ತಿಯಿಂದ ಇದನ್ನು ಪಡೆಯಲಾಗಿದೆ. ಎಐ ನೆರವಿನ ವರ್ಗೀಕರಣದಿಂದಾಗಿ ವಿಭಾಗ ಮತ್ತು ಬಳಸಿರುವ ಪ್ರಾತಿನಿಧಿಕ ಚಿತ್ರ ತಪ್ಪಾಗಿರಬಹುದು. ಸುದ್ದಿಯ ವಿಷಯದಲ್ಲಿ ಯಾವುದೇ ಬದಲಾವಣೆ ಇಲ್ಲ";

export const countPTags = (el) => {
  const html = el?.text;
  if (!html || typeof html !== "string") return 0;
  if (el?.metadata?.["promotional-message"] === true || el?.metadata?.["promotional-message"] === "true") return 0;

  const matches = html.match(/<p(\s|>)/gi);
  return matches ? matches.length : 0;
};

export const getTotalParaCount = (story) => {
  return (
    story?.cards?.reduce((count, card) => {
      card?.["story-elements"]?.forEach((el) => {
        if (el?.type === "text" && el?.subtype === null && el?.text) {
          count += countPTags(el);
        }
      });
      return count;
    }, 0) ?? 0
  );
};

const getWordCount = (html) => {
  if (typeof DOMParser === "undefined") return 0;
  const doc = new DOMParser().parseFromString(html, "text/html");
  return (doc.body.textContent || "").match(/\S+/g)?.length || 0;
};

const truncateHtmlToWords = (html, wordLimit) => {
  const doc = new DOMParser().parseFromString(html, "text/html");
  const walker = doc.createTreeWalker(doc.body, NodeFilter.SHOW_TEXT);
  let wordsRemaining = wordLimit;
  let node;

  while ((node = walker.nextNode())) {
    const words = node.textContent.match(/\S+/g) || [];
    if (words.length <= wordsRemaining) {
      wordsRemaining -= words.length;
      continue;
    }

    const wordEnd = [...node.textContent.matchAll(/\S+/g)][wordsRemaining - 1]?.index;
    const end = wordEnd + words[wordsRemaining - 1].length;
    const range = doc.createRange();
    range.setStart(node, end);
    range.setEnd(doc.body, doc.body.childNodes.length);
    range.deleteContents();
    break;
  }

  return doc.body.innerHTML;
};

const getPremiumCards = (cards, wordLimit = 165) => {
  let wordsRemaining = wordLimit;
  let limitReached = false;

  return cards.reduce((visibleCards, card) => {
    if (limitReached) return visibleCards;

    const visibleElements = [];
    for (const element of card["story-elements"] || []) {
      if (limitReached) break;

      if (wordsRemaining === 0) {
        limitReached = true;
        break;
      }

      if (element.type === "text" && element.text) {
        const isPromotional =
          element.metadata?.["promotional-message"] === true ||
          element.metadata?.["promotional-message"] === "true";
        const isAlsoRead = element.subtype === "also-read";
        if (!isPromotional && !isAlsoRead) {
          const elementWordCount = getWordCount(element.text);
          if (elementWordCount > wordsRemaining) {
            visibleElements.push({ ...element, text: truncateHtmlToWords(element.text, wordsRemaining) });
            wordsRemaining = 0;
            limitReached = true;
            break;
          }
          wordsRemaining -= elementWordCount;
        }
      }

      visibleElements.push(element);
      if (wordsRemaining === 0) limitReached = true;
    }

    if (visibleElements.length) visibleCards.push({ ...card, "story-elements": visibleElements });
    return visibleCards;
  }, []);
};

const CustomStoryElement = ({ element, adType, story, syndicatedPrintFlagID = false, isAdfree = false }) => {
  const rawHtml = element && element.text;
  const [paragraphs, setParagraphs] = useState([]);
  const [isWebView, setIsWebView] = useState(false);
  const storyTemplate = story && story["story-template"];
  const blockedTemplates = ["live-blog", "photo"];

  const totalParaCount = getTotalParaCount(story);

  useEffect(() => {
    if (typeof window !== "undefined") {
      const searchparam = window.location.search;
      if (!searchparam.includes("app=true")) {
        setIsWebView(true);
      }
      const tempDiv = document.createElement("div");
      tempDiv.innerHTML = rawHtml;
      const paragraphElements = getParagraphsArr(rawHtml);
      const parsedParagraphs =
        paragraphElements !== null &&
        paragraphElements.map((p, index) => {
          const tempElement = document.createElement("div");
          tempElement.innerHTML = p;
          // Check if the element contains a <p> tag
          const hasPTag = tempElement.getElementsByTagName("p").length > 0;
          const isPromotional = element?.metadata?.["promotional-message"] === true || element?.metadata?.["promotional-message"] === "true";
          if (hasPTag && !isPromotional) {
            countPara = countPara + 1;
          }

          if (
            countPara > 0 &&
            countPara < totalParaCount &&
            countPara === (story["story-template"] === "listicle" ? 5 : 4) &&
            !hasRenderedMidAd2
          ) {
            hasRenderedMidAd2 = true;
            return (
              <div key={index}>
                <div className="story-element story-element-text" styleName={syndicatedPrintFlagID === element?.id ? "syndicatedPrint-wrapper" : ""} dangerouslySetInnerHTML={{ __html: p }}></div>
                {!isWebView && !isAdfree &&
                  (story["story-template"] === "text" ||
                    story["story-template"] === "syndicated" ||
                    story["story-template"] === "pv-exclusive" ||
                    story["story-template"] === "listicle") && (
                    <>
                      <div styleName="ad-wrapper" className="hide-desktop">
                        <AdContainer
                          AdClassName="standard-336x280"
                          id="div-gpt-ad-1727945329402-0"
                          mobile="PV_MWeb_AT_Mid02"
                          isExternalAdPath={adType === "izooto"}
                          cta={countPara < totalParaCount ? "inStory" : "sidebar"}
                        />
                        {window && window.innerWidth < 767 && (
                          <div
                            id="taboola-mid-article-personalisation"
                            styleName="taboola-4u"
                            ref={(el) => {
                              if (!el) return;

                              window._taboola = window._taboola || [];
                              window._taboola.push({
                                mode: "organic-thumbnails-mid-personalisation-mob",
                                container: "taboola-mid-article-personalisation",
                                placement: "Mid Article Personalisation",
                                target_type: "mix"
                              });
                              window._taboola.push({ flush: true });
                            }}
                          />
                        )}
                      </div>
                      <div styleName="ad-wrapper" className="hide-mobile">
                        <AdContainer
                          AdClassName="standard-728x90"
                          id="div-gpt-ad-1770621301106-0"
                          desktop="PV_Desktop_AP_Mid_02"
                          isExternalAdPath={adType === "izooto"}
                          cta={countPara < totalParaCount ? "inStory" : "sidebar"}
                        />
                        {window && window.innerWidth >= 767 && (
                          <div
                            id="taboola-mid-article-personalisation"
                            ref={(el) => {
                              if (!el) return;

                              window._taboola = window._taboola || [];
                              window._taboola.push({
                                mode: "organic-thumbnails-mid-personalisation-desk",
                                container: "taboola-mid-article-personalisation",
                                placement: "Mid Article Personalisation",
                                target_type: "mix"
                              });
                              window._taboola.push({ flush: true });
                            }}
                          />
                        )}
                      </div>
                    </>
                  )}
              </div>
            );
          }
          if (
            !blockedTemplates.includes(storyTemplate) &&
            countPara > 0 &&
            countPara === (story?.["story-template"] === "listicle" ? 3 : 1) &&
            !hasRenderedMidAd1
          ) {
            hasRenderedMidAd1 = true;
            return (
              <React.Fragment key={index}>
                <div>
                  <div className="story-element story-element-text" styleName={syndicatedPrintFlagID === element?.id ? "syndicatedPrint-wrapper" : ""} dangerouslySetInnerHTML={{ __html: p }}></div>
                </div>
                <NewBodyWidget />
                {storyTemplate !== "podcast" && !isAdfree && (
                  <div className="hide-desktop">
                    <AdContainer
                      AdClassName="standard-336x280"
                      id={adType === "izooto" ? "div-gpt-ad-1711631172416-0" : "div-gpt-ad-1675837516069-0"}
                      mobile={adType === "izooto" ? "PV_MWeb_AP_Mid_ym" : "PV_MWeb_AP_Mid"}
                      isExternalAdPath={adType === "izooto"}
                      cta={countPara < totalParaCount ? "inStory" : "sidebar"}
                    />
                  </div>
                )}
                {!isAdfree && (
                  <div className="hide-mobile">
                    <AdContainer
                      AdClassName="standard-336x280"
                      id={adType === "izooto" ? "div-gpt-ad-1711630758806-0" : "div-gpt-ad-1675837175211-0"}
                      desktop={adType === "izooto" ? "PV_Desktop_AP_Mid_ym" : "PV_Desktop_AP_Mid"}
                      isExternalAdPath={adType === "izooto"}
                      cta={countPara < totalParaCount ? "inStory" : "sidebar"}
                    />
                  </div>
                )}
              </React.Fragment>
            );
          }
          return (
            <div key={index}>
              <div className="story-element story-element-text" styleName={syndicatedPrintFlagID === element?.id ? "syndicatedPrint-wrapper" : ""} dangerouslySetInnerHTML={{ __html: p }}></div>
            </div>
          );
        });
      setParagraphs(parsedParagraphs);
    }
  }, []);

  return <div>{paragraphs}</div>;
};
CustomStoryElement.propTypes = {
  element: object,
  elementIndex: number,
  adType: string,
  story: object
};
const getElements = (element, story, elementIndex, adType, isMobile, syndicatedPrintFlagID = 0, isAdfree = false) => {
  const elementType = element.type;

  if (element.metadata?.["promotional-message"] === true || element.metadata?.["promotional-message"] === "true") {
    return null;
  }

  if (element.type === "text" && element.subtype === null) {
    return (
      <div id="text-element-with-ad">
        <CustomStoryElement
          element={element}
          elementIndex={elementIndex}
          adType={adType}
          isMobile={isMobile}
          story={story}
          syndicatedPrintFlagID={syndicatedPrintFlagID}
          isAdfree={isAdfree}
        />
      </div>
    );
  }
  switch (element.subtype) {
    case "attachment": {
      return <Attachment element={element} />;
    }
    case "references": {
      const referenceObj = get(element, ["story-elements"], []);
      return referenceObj.map((element, index) => <ReferenceElement element={element} key={index} />);
    }
    case "image-gallery": {
      if (element.metadata.type === "gallery") {
        return <WithLazy margin="50px">{() => <ImageGallery element={element} story={story} />}</WithLazy>;
      }

      if (element.metadata.type === "slideshow") {
        return <WithLazy margin="50px">{() => <Slideshow story={story} element={element} />}</WithLazy>;
      }
      break;
    }
    case "blockquote": {
      return (
        <div styleName="blockquote-wrapper">
          <BlockQuoteIcon />
          <StoryElement element={element} key={element.id} story={story} />
        </div>
      );
    }
    case "quote": {
      return (
        <div styleName="quote-wrapper" className="exclusive-wrapper">
          <div styleName="quote-contents" className="exclusive-contents">
            <img src="/icons/quoteicon.png" styleName="quote-icon" />
            <StoryElement element={element} key={element.id} story={story} />
          </div>
        </div>
      );
    }
    case "also-read": {
      return <AlsoRead element={element} story={story} />;
    }
    case "q-and-a": {
      return <QandA element={element} story={story} subtype={element.subtype} />;
    }
    case "question": {
      return <QandA element={element} story={story} subtype={element.subtype} />;
    }
    case "answer": {
      return <QandA element={element} story={story} subtype={element.subtype} />;
    }
    case "table": {
      if (element.metadata["has-header"] == true) {
        const updateEle = { ...element };
        updateEle.metadata["has-header"] = false;
        return (
          <div className="has-head story-element story-element-data">
            <StoryElement element={updateEle} key={updateEle.id} story={story} />;
          </div>
        );
      } else {
        return <StoryElement element={element} key={element.id} story={story} />;
      }
    }
    default:
      if (elementType === "image") {
        if (story["story-template"] === "photo") {
          return (
            <div styleName="share-wrapper">
              {story["story-template"] === "photo" && elementType === "image" && (
                <StoryElement
                  renderTemplates={{ image: ImagewithCaption }}
                  element={element}
                  key={element.id}
                  story={story}
                />
              )}
            </div>
          );
        } else {
          return (
            <StoryElement
              renderTemplates={{ image: ImagewithCaptionWithoutShare }}
              element={element}
              key={element.id}
              story={story}
            />
          );
        }
      } else {
        return (
          <div styleName="share-wrapper">
            {story["story-template"] !== "podcast" ? (
              <StoryElement element={element} key={element.id} story={story} loadIframeOnClick />
            ) : elementType !== "jsembed" ? (
              <StoryElement element={element} key={element.id} story={story} loadIframeOnClick />
            ) : element.subtype === "" || element.subtype === null ? (
              console.log("element.subtype ignored:>:>:>:>:>", elementType, element.subtype)
            ) : (
              <StoryElement element={element} key={element.id} story={story} loadIframeOnClick />
            )}
          </div>
        );
      }
  }
};

export const StoryCard = ({ story, card, cardIndex, adType, index, imgIDArray = [], syndicatedPrintFlagID = 0, isAdfree = false }) => {
  const elements = card["story-elements"];
  const isMobile = useDevice();

  const wrappedAllElementCard = (elements, story) => {
    let allAlsoReads = [];
    const allWrappedElements = [];

    for (let i = 0; i < elements.length; i++) {
      const element = elements[i];

      if (element.subtype === "also-read") {
        allAlsoReads.push(element);
      } else {
        if (allAlsoReads.length > 1) {
          allWrappedElements.push(
            <div styleName="also-read-main-wrapper" key={`also-read-wrapper-${i}`}>
              {allAlsoReads.map((element, elementIndex) => (
                <AlsoRead
                  key={elementIndex}
                  story={story}
                  element={element}
                  isConsecutive={true}
                  elementIndex={elementIndex}
                />
              ))}
            </div>
          );
        } else {
          allWrappedElements.push(
            ...allAlsoReads.map((element, elementIndex) => (
              <AlsoRead
                key={elementIndex}
                story={story}
                element={element}
                isConsecutive={false}
                elementIndex={elementIndex}
              />
            ))
          );
        }

        allAlsoReads = [];
        allWrappedElements.push(getElements(element, story, i, adType, isMobile, syndicatedPrintFlagID, isAdfree));
      }
    }
    if (allAlsoReads.length > 1) {
      allWrappedElements.push(
        <div styleName="also-read-main-wrapper" key="also-read-wrapper-end">
          {allAlsoReads.map((element, elementIndex) => (
            <AlsoRead
              key={elementIndex}
              story={story}
              element={element}
              isConsecutive={true}
              elementIndex={elementIndex}
            />
          ))}
        </div>
      );
    } else {
      allWrappedElements.push(
        ...allAlsoReads.map((element, elementIndex) => (
          <AlsoRead
            key={elementIndex}
            story={story}
            element={element}
            isConsecutive={false}
            elementIndex={elementIndex}
          />
        ))
      );
    }
    return allWrappedElements;
  };
  return (
    <div styleName="story-card" key={card.id} id={card.id} className="story-card">
      {wrappedAllElementCard(elements, story).map((element, elementIndex) => {
        const elementType = element?.props?.children?.props?.element?.type;
        const elementID = element?.props?.children?.props?.element?.id;
        const secondPhotoStoryID = imgIDArray.length ? imgIDArray[1] : null;
        const fourthPhotoStoryID = imgIDArray.length - 1 === 3 ? null : imgIDArray[3] ?? null;
        return (
          <React.Fragment key={elementIndex}>
            <React.Fragment>{element}</React.Fragment>

            {!isAdfree && story?.["story-template"] === "photo" && elementID === secondPhotoStoryID && (
              <div styleName="ad-wrapper">
                {isMobile ? (
                  <AdContainer
                    AdClassName="standard-336x280"
                    id={adType === "izooto" ? "div-gpt-ad-1711631172416-0" : "div-gpt-ad-1675837516069-0"}
                    mobile={adType === "izooto" ? "PV_MWeb_AP_Mid_ym" : "PV_MWeb_AP_Mid"}
                    isExternalAdPath={adType === "izooto"}
                    cta={story?.["story-template"] === "photo" ? "sidebar" : (getTotalParaCount(story) > 0 ? "inStory" : "sidebar")}
                  />
                ) : (
                  <AdContainer
                    AdClassName="standard-336x280"
                    id={adType === "izooto" ? "div-gpt-ad-1711630758806-0" : "div-gpt-ad-1675837516069-0"}
                    desktop={adType === "izooto" ? "PV_Desktop_AP_Mid_ym" : "PV_Desktop_AP_Mid"}
                    isExternalAdPath={adType === "izooto"}
                    cta={story?.["story-template"] === "photo" ? "sidebar" : (getTotalParaCount(story) > 0 ? "inStory" : "sidebar")}
                  />
                )}
              </div>
            )}

            {!isAdfree && story?.["story-template"] === "photo" && elementID === fourthPhotoStoryID && (
              <div styleName="ad-wrapper">
                {isMobile ? (
                  <>
                    <AdContainer
                      AdClassName="standard-336x280"
                      id="div-gpt-ad-1727945329402-0"
                      desktop={false}
                      mobile="PV_MWeb_AT_Mid02"
                      cta={story?.["story-template"] === "photo" ? "sidebar" : (getTotalParaCount(story) > 0 ? "inStory" : "sidebar")}
                    />
                    <div
                      id="taboola-mid-article-personalisation"
                      styleName="taboola-4u"
                      ref={(el) => {
                        if (!el) return;

                        window._taboola = window._taboola || [];
                        window._taboola.push({
                          mode: "organic-thumbnails-mid-personalisation-mob",
                          container: "taboola-mid-article-personalisation",
                          placement: "Mid Article Personalisation",
                          target_type: "mix"
                        });
                        window._taboola.push({ flush: true });
                      }}
                    />
                  </>
                ) : (
                  <>
                    <AdContainer
                      AdClassName="standard-728x90"
                      id="div-gpt-ad-1770621301106-0"
                      desktop="PV_Desktop_AP_Mid_02"
                      mobile={false}
                      cta={story?.["story-template"] === "photo" ? "sidebar" : (getTotalParaCount(story) > 0 ? "inStory" : "sidebar")}
                    />
                    <div
                      id="taboola-mid-article-personalisation"
                      ref={(el) => {
                        if (!el) return;

                        window._taboola = window._taboola || [];
                        window._taboola.push({
                          mode: "organic-thumbnails-mid-personalisation-desk",
                          container: "taboola-mid-article-personalisation",
                          placement: "Mid Article Personalisation",
                          target_type: "mix"
                        });
                        window._taboola.push({ flush: true });
                      }}
                    />
                  </>
                )}
              </div>
            )}
            {!isAdfree && cardIndex === 0 &&
              elementIndex === 0 &&
              story?.["story-template"] !== "photo" &&
              elementType !== "text" ? (
              <div styleName="ad-wrapper">
                {isMobile ? (
                  <AdContainer
                    AdClassName="standard-336x280"
                    id={adType === "izooto" ? "div-gpt-ad-1711631172416-0" : "div-gpt-ad-1675837516069-0"}
                    mobile={adType === "izooto" ? "PV_MWeb_AP_Mid_ym" : "PV_MWeb_AP_Mid"}
                    isExternalAdPath={adType === "izooto"}
                    cta={getTotalParaCount(story) > 0 ? "inStory" : "sidebar"}
                  />
                ) : (
                  <AdContainer
                    AdClassName="standard-336x280"
                    id={adType === "izooto" ? "div-gpt-ad-1711630758806-0" : "div-gpt-ad-1675837516069-0"}
                    desktop={adType === "izooto" ? "PV_Desktop_AP_Mid_ym" : "PV_Desktop_AP_Mid"}
                    isExternalAdPath={adType === "izooto"}
                    cta={getTotalParaCount(story) > 0 ? "inStory" : "sidebar"}
                  />
                )}
              </div>
            ) : null}
          </React.Fragment>
        );
      })}
    </div>
  );
};

const StoryElements = ({
  story,
  index,
  cards,
  isPodcast = false,
  isFactCheck,
  isMovieReview,
  isGadgetReview,
  isSponsoredStory
}) => {
  const imgIDArray = React.useMemo(() => {
    const ids = [];
    cards.forEach((card) => {
      card["story-elements"]?.forEach((el) => {
        if (el.type === "image") {
          ids.push(el.id);
        }
      });
    });
    return ids;
  }, [cards]);
  const template = story["story-template"] ? story["story-template"] : "text";
  const [renderTaboolaAd, setRenderTaboolaAd] = useState(true);
  const [renderAd, setRenderAd] = useState(false);
  const [clipHeight, setClipHeight] = useState(null);
  const contentRef = useRef(null);
  const resizeDebounceRef = useRef(null);
  const isMobile = useDevice();
  const adType = useAdType();
  const qtState = useSelector((state) => get(state, ["qt"], {}));
  const isAdfree = get(qtState, ["config", "subscriber"]) === "true";
  const tags = story.tags || [];
  const isPremiumStory = story?.access === "subscription";
  const [premiumCards, setPremiumCards] = useState(isPremiumStory && !isAdfree ? null : cards);
  const [isAuthReloading, setIsAuthReloading] = useState(false);
  const [isSSOCallback, setIsSSOCallback] = useState(false);
  const isUserLogin = get(qtState, ["user", "isLoggedIn"], false);
  const prevIsUserLoginRef = useRef(isUserLogin);
  const [showLoader, setShowLoader] = useState(true);

  useEffect(() => {
    if (!isPremiumStory || isAdfree || typeof window === "undefined") {
      setPremiumCards(cards);
      return;
    }

    setPremiumCards(getPremiumCards(cards));
  }, [cards, isAdfree, isPremiumStory]);

  useEffect(() => {
    setTimeout(() => {
      setRenderAd(true);
    }, 200);
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      setShowLoader(false);
    }, 500);

    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
      if (typeof window === "undefined") return;
      const searchParams = new URLSearchParams(window.location.search);
      if (
        searchParams.has("access_token") &&
        searchParams.has("refresh_token")
      ) {
        setIsSSOCallback(true);
      }
    }, []);

    useLayoutEffect(() => {
      if (prevIsUserLoginRef.current !== isUserLogin && isSSOCallback) {
        prevIsUserLoginRef.current = isUserLogin;
        setIsAuthReloading(true);
        return;
      }

      prevIsUserLoginRef.current = isUserLogin;
    }, [isUserLogin, isSSOCallback]);

    useEffect(() => {
      if (!isAuthReloading || typeof window === "undefined") return;

      requestAnimationFrame(() => {
        window.location.reload();
      });
    }, [isAuthReloading]);

  useEffect(() => {
    if (typeof window !== "undefined") {
      const search = window.location.search;
      if (search.includes("access_token") && isPremiumStory && isAdfree) {
        setTimeout(() => {
          window.dispatchEvent(
            new CustomEvent("show-snackbar", {
              detail: { type: "info", message: "You already have an active Subscription!" }
            })
          );
        }, 800);
      }
    }
  }, [isAdfree, isPremiumStory]);

  useEffect(() => {
    if (!isPremiumStory || isAdfree) return;

    const measurePaywallPosition = () => {
      if (!contentRef.current) return;
      const container = contentRef.current;
      const WORD_LIMIT = 150;
      const textElements = container.querySelectorAll(".story-element-text");

      if (textElements.length === 0) return;

      let wordCount = 0;
      let targetTextNode = null;
      let targetCharOffset = 0;
      let fallbackEl = null;
      let found = false;

      for (let i = 0; i < textElements.length && !found; i++) {
        const textEl = textElements[i];
        const walker = document.createTreeWalker(textEl, NodeFilter.SHOW_TEXT, null);
        let node;
        while ((node = walker.nextNode()) && !found) {
          const text = node.textContent || "";
          const words = text.match(/\S+/g);
          if (!words) continue;
          if (wordCount + words.length >= WORD_LIMIT) {
            targetTextNode = node;
            fallbackEl = textEl;
            const remaining = WORD_LIMIT - wordCount;
            let idx = 0;
            let wIdx = 0;
            while (wIdx < remaining && idx < text.length) {
              while (idx < text.length && /\s/.test(text[idx])) idx++;
              while (idx < text.length && /\S/.test(text[idx])) idx++;
              wIdx++;
            }
            targetCharOffset = idx;
            found = true;
          } else {
            wordCount += words.length;
          }
        }
      }

      if (!found) {
        // Fewer than 150 words — no clipping needed
        setClipHeight(null);
        return;
      }

      const containerRect = container.getBoundingClientRect();

      // Use Range API for line-level precision
      try {
        const range = document.createRange();
        const start = Math.max(0, targetCharOffset - 1);
        const end = Math.max(start + 1, targetCharOffset);
        if (end <= targetTextNode.length) {
          range.setStart(targetTextNode, start);
          range.setEnd(targetTextNode, end);
          const rect = range.getBoundingClientRect();
          if (rect && rect.bottom > containerRect.top) {
            setClipHeight(Math.round(rect.bottom - containerRect.top));
            return;
          }
        }
      } catch (e) {
        // fall through to element-level fallback
      }

      // Fallback: clip at the element boundary (shows the full paragraph)
      if (fallbackEl) {
        const elRect = fallbackEl.getBoundingClientRect();
        if (elRect.bottom > containerRect.top) {
          setClipHeight(Math.round(elRect.bottom - containerRect.top));
        }
      }
    };

    const scheduleOnce = (() => {
      let scheduled = false;
      return () => {
        if (scheduled) return;
        scheduled = true;
        // Double rAF to ensure browser has computed layout after React's DOM update
        requestAnimationFrame(() => requestAnimationFrame(measurePaywallPosition));
      };
    })();

    // MutationObserver fires as soon as CustomStoryElement inserts text content
    const observer = new MutationObserver(() => {
      if (contentRef.current && contentRef.current.querySelectorAll(".story-element-text").length > 0) {
        observer.disconnect();
        scheduleOnce();
      }
    });

    if (contentRef.current) {
      // Check if content is already in the DOM (e.g. client-side nav)
      if (contentRef.current.querySelectorAll(".story-element-text").length > 0) {
        scheduleOnce();
      } else {
        observer.observe(contentRef.current, { childList: true, subtree: true });
      }
    }

    const handleResize = () => {
      clearTimeout(resizeDebounceRef.current);
      resizeDebounceRef.current = setTimeout(measurePaywallPosition, 200);
    };

    const resizeObserver = new ResizeObserver(handleResize);
    if (contentRef.current) {
      resizeObserver.observe(contentRef.current);
    }
    window.addEventListener("resize", handleResize);

    const pollInterval = setInterval(handleResize, 500);
    const pollTimeout = setTimeout(() => clearInterval(pollInterval), 3000);

    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(handleResize);
    }

    return () => {
      observer.disconnect();
      resizeObserver.disconnect();
      clearTimeout(resizeDebounceRef.current);
      clearInterval(pollInterval);
      clearTimeout(pollTimeout);
      window.removeEventListener("resize", handleResize);
    };
  }, [isPremiumStory, isAdfree, showLoader]);

  let syndicatedPrintFlagID = 0;
  syndicatedPrintFlagID = React.useMemo(() => {
    if (template !== "syndicated-print") return 0;

    const elements = story?.cards?.at(-1)?.["story-elements"] || [];

    const lastElement = elements.at(-1);
    const secondLastElement = elements.at(-2);

    if (lastElement?.text?.includes(syndicatedPrintString)) {
      return lastElement.id;
    }

    if (secondLastElement?.text?.includes(syndicatedPrintString)) {
      return secondLastElement.id;
    }

    return 0;
  }, [story, template]);

  if (
    (isPremiumStory && (showLoader || premiumCards === null)) ||
    isSSOCallback ||
    isAuthReloading
  ) {
    console.log("displaying loader");
      return <SpinnerLoader />;
  }


  return (
    <div styleName="wrapper" style={isPremiumStory ? { backgroundColor: "#FFF8E8" } : {}}>
      {isMovieReview && <MovieReviewView story={story} />}
      {isGadgetReview && <GadgetReviewView story={story} />}
      <div
        id={`story-content-${story.id}`}
        className={`story-cards ${template !== "subhashita" && template !== "cartoon" && template !== "nammakshetra-namma-ooru" && !(isPremiumStory && !isAdfree)
          ? "taboola-read-more-container"
          : ""
          }`}
      >
        <div
          ref={contentRef}
          style={
            isPremiumStory && !isAdfree && clipHeight
              ? { overflow: "hidden", maxHeight: `${clipHeight}px` }
              : {}
          }
        >
          {(isPremiumStory && !isAdfree ? premiumCards : cards).map((card, cardIndex) => (
            <StoryCard
              card={card}
              story={story}
              key={card.id}
              cardIndex={cardIndex}
              index={index}
              imgIDArray={imgIDArray}
              syndicatedPrintFlagID={syndicatedPrintFlagID}
              isAdfree={isAdfree}
            />
          ))}

          {isPodcast && story["first-published-at"] > 1682569800000 ? <PodcastBody /> : null}
          {isFactCheck && <FactCheckView story={story} />}
        </div>
        {isPremiumStory && !isAdfree && <PaywallCard story={story} />}
        <Tags story={story} />
        <PromotionalMessage story={story} />
        {isSponsoredStory && <SponsoredTag />}
        {index === 0 ? (
          <div styleName="ad-wrapper" className="hide-desktop">
            {!isAdfree && <AdContainer
              AdClassName="standard-336x280"
              id={adType === "izooto" ? "div-gpt-ad-1711631239725-0" : "div-gpt-ad-1675837544367-0"}
              mobile={adType === "izooto" ? "PV_MWeb_AP_Bottom_ym" : "PV_MWeb_AP_Bottom"}
              isExternalAdPath={adType === "izooto"}
              cta="sidebar"
            />}
          </div>
        ) : null}

        {isMobile ? (
          <div>
            <WithLazy margin="300px">{() => <LatestNews story={story} />}</WithLazy>
          </div>
        ) : null}
      </div>
      {renderTaboolaAd && !isAdfree ? (
        <TaboolaAd
          container={`taboola-below-article-${story.id}`}
          placement="Below Article Thumbnails"
          mode="thumbnails-e"
        />
      ) : null}
      {template !== "sponsored" && <div styleName="temp-ad"></div>}
    </div>
  );
};

StoryElements.defaultProps = {
  isPodcast: false,
  isFactCheck: false,
  isMovieReview: false,
  isGadgetReview: false,
  isSponsoredStory: false
};

export default StoryElements;
